import { Icon } from '../components/Icon';
import { WHY_PARTNER, SALES_HOOKS } from '../content/site';

/**
 * "Why partner with us" value section + a scannable sales-hooks band.
 *
 * Persuasion applied ethically: each value prop leads with the sponsor's
 * outcome (jobs-to-be-done), then backs it with concrete proof and a specific
 * contrast vs forgettable advertising. Risk-reversal ("flexible funding — we
 * share the risk, never locked in") and social proof (winner content) are
 * surfaced explicitly.
 * Compliance-safe: skill-based, consent-first, no guaranteed-win language.
 */
export function WhyPartner() {
  return (
    <section id="why-partner" className="section bg-white">
      <div className="container-content">
        {/* Sales-hooks band — punchy, scannable, makes a busy exec keep reading */}
        <ul className="grid gap-4 sm:grid-cols-2">
          {SALES_HOOKS.map((item) => (
            <li
              key={item.hook}
              className="flex gap-4 rounded-2xl border border-line bg-surface-alt p-5 sm:p-6"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-aurora text-white">
                <Icon name="check" className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading text-base font-semibold leading-snug text-ink-900 sm:text-lg">
                  {item.hook}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{item.support}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* Value section: outcome-led copy + illustration + proof grid */}
        <div className="mt-16 grid items-center gap-10 lg:mt-20 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="order-2 lg:order-1">
            <figure className="relative overflow-hidden rounded-3xl border border-line bg-aurora-soft shadow-card">
              <img
                src={WHY_PARTNER.image}
                alt={WHY_PARTNER.imageAlt}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] h-full w-full object-cover"
              />
              {/* Risk-reversal callout pinned to the illustration */}
              <figcaption className="absolute bottom-4 left-4 right-4 flex items-start gap-3 rounded-2xl bg-white/90 p-4 shadow-card backdrop-blur">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-aurora text-white">
                  <Icon name="shield" className="h-5 w-5" />
                </span>
                <p className="text-sm leading-snug text-ink-700">
                  <span className="font-semibold text-ink-900">Flexible funding — we share the
                  risk.</span> We fully fund the first few campaigns while we build trust, then every
                  run is open to negotiation — never locked in, never on your own after run one.
                </p>
              </figcaption>
            </figure>
          </div>

          <div className="order-1 lg:order-2">
            <span className="eyebrow">{WHY_PARTNER.eyebrow}</span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-ink-900 sm:text-4xl">
              {WHY_PARTNER.headline}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-ink-500">{WHY_PARTNER.subcopy}</p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href={WHY_PARTNER.cta.href} className="btn-warm">
                {WHY_PARTNER.cta.label}
                <Icon name="arrow-right" className="h-5 w-5" />
              </a>
              <a href={WHY_PARTNER.secondaryCta.href} className="btn-secondary">
                {WHY_PARTNER.secondaryCta.label}
              </a>
            </div>
          </div>
        </div>

        {/* Proof grid: outcome (their job) + proof (our substance) */}
        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_PARTNER.props.map((prop) => (
            <li
              key={prop.title}
              className="flex flex-col rounded-2xl border border-line bg-white p-6 shadow-card transition-shadow duration-200 hover:shadow-card-hover"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-aurora text-white">
                <Icon name={prop.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-ink-900">{prop.title}</h3>
              <p className="mt-2 text-sm font-medium leading-relaxed text-ink-700">{prop.outcome}</p>
              <p className="mt-3 border-t border-line pt-3 text-sm leading-relaxed text-ink-500">
                {prop.proof}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default WhyPartner;
